import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CheckCircleFilled, CloseCircleFilled, CheckOutlined, WarningOutlined } from '@ant-design/icons';
import { User, useSendCognitoSetupMutation, useUpdateUserMfaSettingsMutation } from '../../graphql/types';
import { dispatch } from '../../store';
import { openSnackbar } from '../../store/reducers/snackbar';
import ActionConfirmation from '../../components/mca/ActionConfirmation';
import CustomTooltip from '../../components/CustomTooltip';

interface Props {
  entity: User;
  onRefresh?: () => void;
}

const ACCESS_TOKEN_TTL_OPTIONS = [
  { value: 4, label: '4 hours' },
  { value: 12, label: '12 hours' },
  { value: 24, label: '24 hours' }
];

const DEVICE_REMEMBERING_DAYS_OPTIONS = [1, 3, 7, 30, 90];

const SecurityTab = ({ entity, onRefresh }: Props) => {
  const theme = useTheme();
  const [mfaEnabled, setMfaEnabled] = useState(entity.mfaEnabled);
  const [accessTokenTtlHours, setAccessTokenTtlHours] = useState(entity.accessTokenTtlHours);
  const [mfaDeviceRememberingEnabled, setMfaDeviceRememberingEnabled] = useState(entity.mfaDeviceRememberingEnabled);
  const [deviceRememberingDays, setDeviceRememberingDays] = useState(entity.deviceRememberingDays);
  const [openCognitoConfirm, setOpenCognitoConfirm] = useState(false);

  const [sendCognitoSetup, { loading: cognitoLoading }] = useSendCognitoSetupMutation();
  const [updateMfaSettings, { loading: mfaLoading }] = useUpdateUserMfaSettingsMutation();

  const handleCognitoSetup = async (approved: boolean) => {
    setOpenCognitoConfirm(false);
    if (!approved) return;
    try {
      await sendCognitoSetup({ variables: { id: entity.id } });
      dispatch(
        openSnackbar({
          open: true,
          message: 'Cognito setup email sent successfully.',
          variant: 'alert',
          alert: { color: 'success' },
          close: true
        })
      );
      onRefresh?.();
    } catch (err: any) {
      dispatch(
        openSnackbar({
          open: true,
          message: err?.message || 'Failed to send Cognito setup email.',
          variant: 'alert',
          alert: { color: 'error' },
          close: true
        })
      );
    }
  };

  const handleMfaSave = async () => {
    try {
      await updateMfaSettings({
        variables: {
          dto: {
            id: entity.id,
            mfaEnabled,
            accessTokenTtlHours,
            mfaDeviceRememberingEnabled: mfaEnabled ? mfaDeviceRememberingEnabled : false,
            deviceRememberingDays
          }
        }
      });
      dispatch(
        openSnackbar({
          open: true,
          message: 'MFA settings updated.',
          variant: 'alert',
          alert: { color: 'success' },
          close: true
        })
      );
      onRefresh?.();
    } catch (err: any) {
      dispatch(
        openSnackbar({
          open: true,
          message: err?.message || 'Failed to update MFA settings.',
          variant: 'alert',
          alert: { color: 'error' },
          close: true
        })
      );
    }
  };

  const renderCognitoStatus = () => {
    if (!entity.cognitoSub) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CloseCircleFilled style={{ color: theme.palette.grey[400], fontSize: 18 }} />
          <Typography variant="body2" color="text.secondary">Cognito not configured</Typography>
        </Stack>
      );
    }
    if (!entity.mfaConfigured) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckCircleFilled style={{ color: theme.palette.warning.main, fontSize: 18 }} />
          <Typography variant="body2" color="warning.main">Cognito active, MFA pending setup</Typography>
        </Stack>
      );
    }
    if (!entity.mfaEnabled) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckCircleFilled style={{ color: theme.palette.info.main, fontSize: 18 }} />
          <Typography variant="body2" color="info.main">MFA configured but disabled</Typography>
        </Stack>
      );
    }
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckCircleFilled style={{ color: theme.palette.success.main, fontSize: 18 }} />
        <Typography variant="body2" color="success.main">MFA active</Typography>
      </Stack>
    );
  };

  const hasCognito = !!entity.cognitoSub;

  return (
    <Stack spacing={4} sx={{ pt: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1" fontWeight={600}>Cognito Setup</Typography>
        {renderCognitoStatus()}
        {entity.isActive && entity.isLoginEnabled && (
          <Box>
            <Button
              variant="outlined"
              onClick={() => setOpenCognitoConfirm(true)}
              disabled={cognitoLoading || !entity.isActive}
            >
              Send Cognito Setup Email
            </Button>
          </Box>
        )}
      </Stack>

      <Stack spacing={2} sx={{ opacity: hasCognito ? 1 : 0.5, pointerEvents: hasCognito ? 'auto' : 'none' }}>
        <Typography variant="subtitle1" fontWeight={600}>MFA Settings</Typography>

        {!hasCognito && (
          <Typography variant="caption" color="text.secondary">
            User must have Cognito configured before MFA can be managed.
          </Typography>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={mfaEnabled}
              onChange={(e) => setMfaEnabled(e.target.checked)}
              disabled={!hasCognito}
            />
          }
          label="MFA Required"
        />

        {mfaEnabled && !entity.mfaConfigured && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningOutlined /> User has not completed MFA setup yet
          </Typography>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={mfaDeviceRememberingEnabled}
                onChange={(e) => setMfaDeviceRememberingEnabled(e.target.checked)}
                disabled={!hasCognito || !mfaEnabled}
              />
            }
            label="Trust User Devices for"
            sx={{ mr: 0 }}
          />
          <Select
            size="small"
            value={deviceRememberingDays}
            onChange={(e) => setDeviceRememberingDays(Number(e.target.value))}
            disabled={!hasCognito || !mfaEnabled || !mfaDeviceRememberingEnabled}
            sx={(theme) => ({
              minWidth: 70,
              '& .MuiSelect-select': {
                ...theme.typography.body1
              }
            })}
          >
            {DEVICE_REMEMBERING_DAYS_OPTIONS.map((val) => (
              <MenuItem key={val} value={val}>
                {val}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body1">days</Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="body2">Access Token TTL</Typography>
          <Select
            size="small"
            value={accessTokenTtlHours}
            onChange={(e) => setAccessTokenTtlHours(Number(e.target.value))}
            disabled={!hasCognito}
            sx={{ maxWidth: 200 }}
          >
            {ACCESS_TOKEN_TTL_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Box>
          <Button
            variant="contained"
            startIcon={<CheckOutlined />}
            onClick={handleMfaSave}
            disabled={!hasCognito || mfaLoading}
          >
            Save MFA Settings
          </Button>
        </Box>
      </Stack>

      <ActionConfirmation
        title={`A Cognito setup email will be sent to '${entity.email}'. When the user follows the link, their existing Cognito account and MFA will be reset and reconfigured. Proceed?`}
        open={openCognitoConfirm}
        type="warning"
        handleClose={handleCognitoSetup}
      />
    </Stack>
  );
};

export default SecurityTab;
